import React, { memo } from 'react';
import { CheckIcon, BookOpenIcon, PrinterIcon, LifeBuoyIcon, ShieldCheckIcon, ClockIcon, MessageCircleIcon, AwardIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function IndividualsPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1451">
      <TopBar data-id="element-1452" />
      <Header data-id="element-1453" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1454">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1455">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1456">
            <div className="inline-block bg-[#631bb7] bg-opacity-10 text-[#631bb7] px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-1457">
              For Individuals
            </div>
            <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1458">
              You're Smarter Than This.
              <br data-id="element-1459" />
              <span className="text-gray-500" data-id="element-1460">
                So Why Does Tech Make You Feel Stupid?
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-1461">
              You've built a career. Raised a family. Managed a mortgage. But
              every "update your password" notification feels like a pop quiz
              you didn't study for.{' '}
              <strong className="text-[#111111]" data-id="element-1462">
                Caveo speaks plain English
              </strong>{' '}
              — no judgment, no jargon, just answers.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4" data-id="element-1463">
              <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1464">
                See What You'll Learn
              </Link>
            </div>
            <p className="text-gray-500" data-id="element-1465">
              No credit card. Start with the basics.
            </p>
          </div>
        </div>
      </section>

      {/* Problem/Agitation Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1466">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1467">
          <div className="max-w-3xl mx-auto" data-id="element-1468">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-8 text-center" data-id="element-1469">
              You Shouldn't Need a Computer Science Degree to Check Your Email
              Safely
            </h2>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-1470">
              <p data-id="element-1471">
                <strong className="text-[#111111]" data-id="element-1472">
                  You're not technologically illiterate.
                </strong>{' '}
                You just didn't grow up with this stuff.
              </p>

              <p data-id="element-1473">
                But everywhere you turn, someone's talking about VPNs and
                firewalls and two-factor something-or-other like you're supposed
                to just <em data-id="element-1474">know</em> what that means.
              </p>

              <p data-id="element-1475">
                Your bank sends you alerts about "suspicious activity" but
                doesn't explain what made it suspicious. Your email provider
                says "enable MFA" without defining MFA. YouTube tutorials assume
                you already understand the basics.
              </p>

              <p data-id="element-1476">
                And the security companies? They're happy to sell you $15/month
                monitoring services while never actually teaching you what
                you're being monitored <em data-id="element-1477">for</em>.
              </p>

              <div className="bg-white rounded-2xl p-8 border-2 border-[#631bb7] border-opacity-30 my-8" data-id="element-1478">
                <p className="text-xl text-[#111111] font-semibold text-center mb-0" data-id="element-1479">
                  Here's the truth: You don't need someone watching you.
                  <br data-id="element-1480" />
                  <span className="text-[#631bb7]" data-id="element-1481">
                    You need someone to finally explain this in a way that makes
                    sense.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution/Benefit Section */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1482">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1483">
          <div className="max-w-4xl mx-auto" data-id="element-1484">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4 text-center" data-id="element-1485">
              Learn Digital Security Like a Normal Human
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12" data-id="element-1486">
              Caveo isn't another monitoring app. We're the patient tech expert
              who actually takes the time to explain.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1487">
              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1488">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1489">
                  <MessageCircleIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1490" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1491">
                  Plain English, always
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1492">
                  No "multi-factor authentication protocols." Just "here's how
                  to add an extra password step, and here's why it matters."
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1493">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1494">
                  <ShieldCheckIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1495" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1496">
                  Start where you are
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1497">
                  Never touched a password manager? That's fine. We begin with
                  what threatens you most — password reuse, phishing emails,
                  backup basics — then build from there.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1498">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1499">
                  <CheckIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1500" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1501">
                  Honest guidance
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1502">
                  We'll tell you that most people don't need a VPN. We'll
                  explain why that antivirus software you bought at Best Buy is
                  probably fine. We're not here to sell you more stuff.
                </p>
              </div>

              <div className="bg-[#f8f8f8] rounded-2xl p-8 border-2 border-gray-200" data-id="element-1503">
                <div className="w-12 h-12 bg-[#631bb7] bg-opacity-10 rounded-xl flex items-center justify-center mb-4" data-id="element-1504">
                  <ClockIcon className="w-6 h-6 text-[#631bb7]" data-id="element-1505" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-1506">
                  Learn at your pace
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1507">
                  No webinars to attend. No scheduled classes. Just 28 lessons
                  you can do whenever you have 10 minutes and a cup of coffee.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-1508">
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1509">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1510">
                  Emotional Outcome
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1511">
                  <strong data-id="element-1512">Confidence.</strong> For the first time, you'll
                  understand <em data-id="element-1513">why</em> you're being asked to do these things
                  — and you'll know how to do them right.
                </p>
              </div>
              <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20" data-id="element-1514">
                <p className="text-sm font-semibold text-[#631bb7] uppercase tracking-wide mb-2" data-id="element-1515">
                  Functional Outcome
                </p>
                <p className="text-[#111111] font-medium" data-id="element-1516">
                  <strong data-id="element-1517">Stop feeling vulnerable.</strong> Stop clicking
                  "remind me later" on security prompts because you don't
                  understand them. Take control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof/Trust Section */}
      <section className="w-full bg-[#1a0647] py-16 md:py-24" data-id="element-1518">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1519">
          <div className="max-w-4xl mx-auto" data-id="element-1520">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" data-id="element-1521">
              Built by Someone Who Watched His Own Father Get Scammed
            </h2>

            <div className="bg-white bg-opacity-10 rounded-2xl p-8 mb-12" data-id="element-1522">
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1523">
                Jason Phillips founded Caveo after his dad lost money to cyber
                fraud — not because his dad was careless, but because nobody had
                ever explained the warning signs in language that made sense.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed mb-6" data-id="element-1524">
                After 15 years at tech companies like Asana, Drift, and
                Quantcast, Jason saw the pattern:{' '}
                <strong className="text-[#00f4f8]" data-id="element-1525">
                  The digital security industry profits from confusion.
                </strong>{' '}
                They'd rather sell you monitoring than teach you prevention.
              </p>
              <p className="text-lg text-white text-opacity-90 leading-relaxed" data-id="element-1526">
                So he built something different. A service designed for people
                who deserve clear answers, not more jargon.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1527">
              <div className="bg-white rounded-2xl p-6" data-id="element-1528">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1529">
                  "I'm 67 years old and I finally understand what two-factor
                  authentication does. Took Caveo 5 minutes to explain what my
                  grandkids couldn't in five years."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1530">— Margaret K.</p>
                <p className="text-sm text-gray-500" data-id="element-1531">Retired teacher</p>
              </div>
              <div className="bg-white rounded-2xl p-6" data-id="element-1532">
                <p className="text-gray-600 leading-relaxed mb-4 italic" data-id="element-1533">
                  "No condescension. No eye-rolling. Just someone who takes the
                  time to teach. That's worth way more than another monitoring
                  app."
                </p>
                <p className="font-bold text-[#111111]" data-id="element-1534">— Robert T.</p>
                <p className="text-sm text-gray-500" data-id="element-1535">Age 58</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Breakdown */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1536">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1537">
          <div className="max-w-4xl mx-auto" data-id="element-1538">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-12 text-center" data-id="element-1539">
              What You Get
            </h2>

            <div className="space-y-8" data-id="element-1540">
              <div className="flex items-start" data-id="element-1541">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1542">
                  <BookOpenIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1543" />
                </div>
                <div data-id="element-1544">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1545">
                    28 Lessons Built for Adults
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1546">
                    Not "tech tips" written for 20-year-olds. Real education
                    designed for people who have better things to do than
                    memorize technical terms. Each lesson: one skill, plain
                    English, 10 minutes or less.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1547">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1548">
                  <ShieldCheckIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1549" />
                </div>
                <div data-id="element-1550">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1551">
                    Start With What Matters Most
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1552">
                    We don't make you learn everything at once. Level 1 covers
                    the highest-risk vulnerabilities first (password hygiene,
                    two-factor, backups). You're protected before you're
                    perfect.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1553">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1554">
                  <PrinterIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1555" />
                </div>
                <div data-id="element-1556">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1557">
                    Visual Guides You Can Print
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1558">
                    Quick-reference cards for common tasks. No hunting through
                    videos. Just clear, step-by-step instructions you can keep
                    next to your computer.
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-1559">
                <div className="w-14 h-14 bg-[#631bb7] bg-opacity-10 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0" data-id="element-1560">
                  <LifeBuoyIcon className="w-7 h-7 text-[#631bb7]" data-id="element-1561" />
                </div>
                <div data-id="element-1562">
                  <h3 className="text-xl font-bold text-[#111111] mb-2" data-id="element-1563">
                    Crisis Response Plans
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1564">
                    If something goes wrong, you'll know exactly what to do.
                    Step-by-step recovery plans for locked accounts, suspicious
                    charges, lost devices — everything.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1565">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1566">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1567">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1568">
              Stop Feeling Vulnerable.
              <br data-id="element-1569" />
              Start Feeling Capable.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1570">
              You don't need someone watching your accounts 24/7. You need
              someone to finally explain this stuff like you're an intelligent
              adult.
            </p>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 mb-8" data-id="element-1571">
              <p className="text-lg text-gray-600 mb-6" data-id="element-1572">
                For less than one coffee per month:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-md mx-auto mb-6" data-id="element-1573">
                {['28 plain-English security lessons', 'Visual quick-reference guides', 'Crisis response plans', 'Honest guidance (no upsells)', 'Learn at your own pace'].map((item, index) => <div key={index} className="flex items-center" data-id="element-1574">
                    <CheckIcon className="w-5 h-5 text-[#631bb7] mr-2 flex-shrink-0" data-id="element-1575" />
                    <span className="text-[#111111]" data-id="element-1576">{item}</span>
                  </div>)}
              </div>
              <div className="text-center mb-6" data-id="element-1577">
                <span className="text-4xl font-extrabold text-[#111111]" data-id="element-1578">
                  $5.99
                </span>
                <span className="text-gray-500 text-lg" data-id="element-1579">/month</span>
                <p className="text-gray-500 text-sm mt-1" data-id="element-1580">
                  No contract. Cancel anytime.
                </p>
              </div>
              <Link to="/intro-assessment" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md w-full sm:w-auto" data-id="element-1581">
                Start Learning Today
              </Link>
            </div>

            <div className="bg-[#631bb7] bg-opacity-5 rounded-xl p-6 border border-[#631bb7] border-opacity-20 mb-6" data-id="element-1582">
              <div className="flex items-center justify-center mb-2" data-id="element-1583">
                <AwardIcon className="w-5 h-5 text-[#631bb7] mr-2" data-id="element-1584" />
                <span className="font-bold text-[#111111]" data-id="element-1585">
                  The Caveo Promise
                </span>
              </div>
              <p className="text-gray-600" data-id="element-1586">
                If you don't feel more confident in the first 30 days, we'll
                refund you immediately. No questions.
              </p>
            </div>

            <p className="text-gray-500 text-sm" data-id="element-1587">
              Join 2,000+ adults who decided they were done feeling stupid about
              technology.
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-1588" />
    </div>;
}